import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import { Card } from "antd";
import React, { useEffect, useState } from "react";

import { ModelEnum } from "./types";
import { RequirementEditor } from "./editor";
import { RequirementHeader } from "./header";
import { ShowError } from "../../components/error";
import { useRequirements } from "./hooks";
import { getExistedSet, getRemovedSet, getUpdatedSet } from "./utils";
import { MODIFY_HCP_REQUIREMENTS, GET_HCP_REQUIREMENTS } from "./gql";
import ExpTag from "../../components/ExpTag";
import { EXPIRY_TYPE } from "../requirements/constants";
import moment from "moment-timezone";
import { showErrorToast } from "@src/appV2/lib/Notifications";

function HcpRequirement({ hcpId, agentTMZ }) {
  const [isEditorVisible, showEditor] = useState(false);
  const [modifyHcpRequirements] = useMutation(MODIFY_HCP_REQUIREMENTS);
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading, error, data } = useRequirements({
    id: hcpId,
    model: ModelEnum.HCP,
  });
  const { data: { allRequirements } = { allRequirements: [] } } = useRequirements();
  const [nonExpiringRequirements, setNonExpiringRequirements] = useState(new Set());

  useEffect(() => {
    if (!allRequirements?.length) {
      return;
    }
    const allNonExpiringRequirements = allRequirements.flatMap((requirement) =>
      requirement.expiryType === EXPIRY_TYPE.DOES_NOT_EXPIRE ? [requirement?._id] : []
    );
    setNonExpiringRequirements(new Set(allNonExpiringRequirements));
  }, [allRequirements]);

  const getNextData = (existed, modified) => {
    const existedSet = getExistedSet(existed);
    const removedSet = getRemovedSet(modified);
    const updatedSet = getUpdatedSet(modified);

    const isKept = (id) => !removedSet.has(id) && !updatedSet.has(id);

    const kept = existed.filter((rec) => isKept(rec._id));
    const updated = modified.filter((rec) => updatedSet.has(rec._id));
    const added = modified.filter((rec) => !existedSet.has(rec._id));

    return { hcpRequirementList: [...kept, ...updated, ...added] };
  };

  const updateCache = (cache, { data: { modifiedRequirements } }) => {
    const prevData = cache.readQuery({
      query: GET_HCP_REQUIREMENTS,
      variables: { hcpId },
    });
    const nextData = getNextData(prevData.hcpRequirementList, modifiedRequirements);
    cache.writeQuery({
      query: GET_HCP_REQUIREMENTS,
      variables: { hcpId },
      data: nextData,
    });
  };

  const handleFinish = async ({ newRequirements, removedRequirements, updatedRequirements }) => {
    showEditor(false);
    if (!newRequirements && !removedRequirements && !updatedRequirements) {
      return;
    }
    const payload = {
      hcpId,
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-shadow
      newRequirements: newRequirements?.map(({ expiry, ...data }) => ({
        expiry: expiry ? moment.tz(expiry, agentTMZ).endOf("day").utc().format() : null,
        ...data,
      })),
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-shadow
      updatedRequirements: updatedRequirements?.map(({ expiry, ...data }) => ({
        expiry: expiry ? moment.tz(expiry, agentTMZ).endOf("day").utc().format() : null,
        ...data,
      })),
      removedRequirements,
    };

    try {
      await modifyHcpRequirements({
        variables: payload,
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        update: updateCache as any,
      });
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      showErrorToast("Something went wrong");
    }
  };

  return (
    <Card
      size="small"
      title={<RequirementHeader showEditor={showEditor} title="Fulfilled Requirements" />}
    >
      <ShowError message="Unable to load the requirements" error={error} />
      {data.hcpRequirementList?.map((rec) => (
        <ExpTag
          popover
          key={rec._id}
          expires={rec.expiry}
          isNonExpiringRequirement={nonExpiringRequirements.has(rec?.requirement?._id)}
        >
          {rec.requirement.name}
        </ExpTag>
      ))}
      <RequirementEditor
        initial={data.hcpRequirementList}
        model={ModelEnum.HCP}
        onFinish={handleFinish}
        visible={isEditorVisible}
      />
    </Card>
  );
}

HcpRequirement.propTypes = {
  hcpId: PropTypes.string.isRequired,
};

export { HcpRequirement };
